import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Shield, ArrowLeft, Loader2, Save, Mail, CreditCard, Server, CheckCircle2, XCircle, Info, RefreshCw } from 'lucide-react';
import { adminGetSettings, adminUpdateSettings, adminTestM365 } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function AdminSettings() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(null);
  const [testing, setTesting] = useState(false);

  // Local editable state — secrets are only sent when user types them
  const [paypal, setPaypal] = useState({});
  const [smtp, setSmtp] = useState({});
  const [m365, setM365] = useState({});

  const load = async () => {
    try {
      const s = await adminGetSettings();
      setSettings(s);
      setPaypal({ ...s.paypal });
      setSmtp({ ...s.smtp });
      setM365({ ...s.m365 });
    } catch { toast.error('Errore caricamento'); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) { nav('/'); return; }
    load();
  }, [user, authLoading, nav]);

  if (!settings) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0d63b0]" /></div>;

  const savePayPal = async () => {
    setSaving('paypal');
    try {
      const body = { paypal: {
        mode: paypal.mode,
        client_id: paypal.client_id,
        enabled: paypal.enabled,
        ...(paypal.client_secret ? { client_secret: paypal.client_secret } : {}),
      } };
      const s = await adminUpdateSettings(body);
      setSettings(s); setPaypal({ ...s.paypal });
      toast.success('PayPal salvato');
    } catch { toast.error('Errore'); } finally { setSaving(null); }
  };

  const saveSmtp = async () => {
    setSaving('smtp');
    try {
      const body = { smtp: {
        host: smtp.host, port: smtp.port ? Number(smtp.port) : undefined,
        username: smtp.username, from_email: smtp.from_email, from_name: smtp.from_name,
        use_tls: smtp.use_tls, enabled: smtp.enabled,
        ...(smtp.password ? { password: smtp.password } : {}),
      } };
      const s = await adminUpdateSettings(body);
      setSettings(s); setSmtp({ ...s.smtp });
      toast.success('SMTP salvato');
    } catch { toast.error('Errore'); } finally { setSaving(null); }
  };

  const saveM365 = async () => {
    setSaving('m365');
    try {
      const body = { m365: {
        tenant_id: m365.tenant_id, client_id: m365.client_id,
        from_email: m365.from_email, from_name: m365.from_name, enabled: m365.enabled,
        ...(m365.client_secret ? { client_secret: m365.client_secret } : {}),
      } };
      const s = await adminUpdateSettings(body);
      setSettings(s); setM365({ ...s.m365 });
      toast.success('Microsoft 365 salvato');
    } catch { toast.error('Errore'); } finally { setSaving(null); }
  };

  const testM365Connection = async () => {
    setTesting(true);
    try {
      const res = await adminTestM365();
      if (res.ok) toast.success('Connessione M365 riuscita', { description: `Token valido per ${res.expires_in}s` });
      else toast.error('Connessione M365 fallita', { description: (res.error || '').slice(0, 200) });
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail || 'Errore';
      toast.error('Test fallito', { description: d });
    } finally { setTesting(false); }
  };

  return (
    <div className="py-10">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Admin</div>
            <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-slate-900">Impostazioni piattaforma</h1>
            <p className="mt-1 text-slate-600 text-sm">Configura pagamenti, email e integrazioni per l'intera piattaforma.</p>
          </div>
          <Button asChild variant="outline"><Link to="/admin"><ArrowLeft className="h-4 w-4 mr-1.5" /> Torna al pannello</Link></Button>
        </div>

        {/* Microsoft 365 */}
        <Card className="border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Mail className="h-4 w-4 text-[#0d63b0]" /> Microsoft 365 (invio email OTP)</div>
              <p className="mt-1 text-xs text-slate-500">Usa Microsoft Graph API con Client Credentials Flow. Registra un'app in Azure AD con permesso applicativo <code className="font-mono bg-slate-100 rounded px-1">Mail.Send</code>.</p>
            </div>
            <div className="flex items-center gap-2">
              <ConnectionBadge status={settings.m365?.connection_status} />
              <div className="flex items-center gap-2">
                <Label className="text-xs text-slate-600">Abilitato</Label>
                <Switch checked={!!m365.enabled} onCheckedChange={(v) => setM365((s) => ({ ...s, enabled: v }))} />
              </div>
            </div>
          </div>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div>
              <Label>Tenant ID</Label>
              <Input value={m365.tenant_id || ''} onChange={(e) => setM365((s) => ({ ...s, tenant_id: e.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Client ID (App Registration)</Label>
              <Input value={m365.client_id || ''} onChange={(e) => setM365((s) => ({ ...s, client_id: e.target.value }))} placeholder="00000000-0000-0000-0000-000000000000" className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Client Secret</Label>
              <Input type="password" value={m365.client_secret || ''} onChange={(e) => setM365((s) => ({ ...s, client_secret: e.target.value }))} placeholder={settings.m365?.has_client_secret ? '•••••••• (già salvato)' : 'Inserisci il secret'} className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Email mittente (mailbox in tenant)</Label>
              <Input type="email" value={m365.from_email || ''} onChange={(e) => setM365((s) => ({ ...s, from_email: e.target.value }))} placeholder="noreply@tuodominio.it" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Nome mittente</Label>
              <Input value={m365.from_name || ''} onChange={(e) => setM365((s) => ({ ...s, from_name: e.target.value }))} placeholder="Secure Push" className="mt-1" />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2 justify-between">
            <div className="text-xs text-slate-500">
              {settings.m365?.last_test_at && <span>Ultimo test: {new Date(settings.m365.last_test_at).toLocaleString('it-IT')}</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={testM365Connection} disabled={testing || !m365.tenant_id || !m365.client_id}>
                {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Testa connessione
              </Button>
              <Button onClick={saveM365} disabled={saving === 'm365'} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                {saving === 'm365' ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Salva M365</>)}
              </Button>
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50/60 p-3 text-xs text-blue-900 flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <strong>Setup rapido:</strong> Azure Portal → App registrations → New registration → API permissions → Aggiungi <code className="font-mono">Mail.Send</code> (Application) → Grant admin consent → Certificates & secrets → New client secret.
            </div>
          </div>
        </Card>

        {/* SMTP fallback */}
        <Card className="border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2"><Server className="h-4 w-4 text-[#0d63b0]" /> SMTP (fallback / alternativa)</div>
              <p className="mt-1 text-xs text-slate-500">Se M365 non è configurato, usa SMTP tradizionale (es. Aruba, SendGrid, Amazon SES).</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-600">Abilitato</Label>
              <Switch checked={!!smtp.enabled} onCheckedChange={(v) => setSmtp((s) => ({ ...s, enabled: v }))} />
            </div>
          </div>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div>
              <Label>Host</Label>
              <Input value={smtp.host || ''} onChange={(e) => setSmtp((s) => ({ ...s, host: e.target.value }))} placeholder="smtp.example.com" className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Porta</Label>
              <Input type="number" value={smtp.port || ''} onChange={(e) => setSmtp((s) => ({ ...s, port: e.target.value }))} placeholder="587" className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Username</Label>
              <Input value={smtp.username || ''} onChange={(e) => setSmtp((s) => ({ ...s, username: e.target.value }))} placeholder="apikey / user" className="mt-1" />
            </div>
            <div>
              <Label>Password / API key</Label>
              <Input type="password" value={smtp.password || ''} onChange={(e) => setSmtp((s) => ({ ...s, password: e.target.value }))} placeholder={settings.smtp?.has_password ? '•••••••• (già salvato)' : 'Inserisci password'} className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label>Email mittente</Label>
              <Input type="email" value={smtp.from_email || ''} onChange={(e) => setSmtp((s) => ({ ...s, from_email: e.target.value }))} placeholder="noreply@tuodominio.it" className="mt-1" />
            </div>
            <div>
              <Label>Nome mittente</Label>
              <Input value={smtp.from_name || ''} onChange={(e) => setSmtp((s) => ({ ...s, from_name: e.target.value }))} placeholder="Secure Push" className="mt-1" />
            </div>
            <div className="md:col-span-2 flex items-center gap-2 text-sm">
              <Switch checked={smtp.use_tls !== false} onCheckedChange={(v) => setSmtp((s) => ({ ...s, use_tls: v }))} />
              <span>Usa TLS/STARTTLS (consigliato)</span>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={saveSmtp} disabled={saving === 'smtp'} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
              {saving === 'smtp' ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Salva SMTP</>)}
            </Button>
          </div>
        </Card>

        {/* PayPal */}
        <Card className="border border-slate-200 p-6 mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-semibold text-slate-900 flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#0d63b0]" /> PayPal (pagamenti abbonamenti)</div>
              <p className="mt-1 text-xs text-slate-500">Credenziali PayPal Developer. Quando non configurate, i pagamenti restano in modalità mock.</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-600">Abilitato</Label>
              <Switch checked={!!paypal.enabled} onCheckedChange={(v) => setPaypal((s) => ({ ...s, enabled: v }))} />
            </div>
          </div>
          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div>
              <Label>Modalità</Label>
              <select value={paypal.mode || 'sandbox'} onChange={(e) => setPaypal((s) => ({ ...s, mode: e.target.value }))} className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="sandbox">Sandbox (test)</option>
                <option value="live">Live (produzione)</option>
              </select>
            </div>
            <div>
              <Label>Client ID</Label>
              <Input value={paypal.client_id || ''} onChange={(e) => setPaypal((s) => ({ ...s, client_id: e.target.value }))} placeholder="AXX...XX" className="mt-1 font-mono text-xs" />
            </div>
            <div className="md:col-span-2">
              <Label>Client Secret</Label>
              <Input type="password" value={paypal.client_secret || ''} onChange={(e) => setPaypal((s) => ({ ...s, client_secret: e.target.value }))} placeholder={settings.paypal?.has_client_secret ? '•••••••• (già salvato)' : 'Inserisci il secret'} className="mt-1 font-mono text-xs" />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={savePayPal} disabled={saving === 'paypal'} className="bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
              {saving === 'paypal' ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-1.5" /> Salva PayPal</>)}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ConnectionBadge({ status }) {
  if (status === 'ok') return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Connesso</span>;
  if (status === 'failed') return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2.5 py-1 text-xs font-semibold"><XCircle className="h-3.5 w-3.5" /> Errore</span>;
  return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 px-2.5 py-1 text-xs font-semibold">Non testato</span>;
}
